import { FC } from 'react';
import { Box, keyframes, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { useTrans } from '@libs/i18n';
import { CircleNotchIcon } from '@phosphor-icons/react';

import { ADMIN_STATUS } from './adminTheme';

const spin = keyframes`to { transform: rotate(360deg); }`;

interface Props {
  /** Raw status string from the API (case-insensitive). */
  status: string | null | undefined;
}

/**
 * Pill-shaped status chip mirroring the design's `Pill` — soft tinted
 * background, colored border, a leading dot (or spinner for in-progress
 * states) and a localized label. Falls back to the neutral `draft` tone and
 * the raw status text for values not in the map.
 */
export const AdminStatusChip: FC<Props> = observer(({ status }) => {
  const theme = useTheme();
  const { t } = useTrans();
  const key = (status ?? '').toLowerCase();
  const tone = ADMIN_STATUS[key];
  const label = tone ? t(tone.labelKey) : status || t('app.admin.status.unknown');
  const bg = tone?.bg ?? theme.palette.grey[200];
  const border = tone?.border ?? theme.palette.grey[400];
  const fg = tone?.fg ?? theme.palette.text.secondary;

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        backgroundColor: bg,
        border: `1px solid ${border}`,
        color: fg,
        borderRadius: 999,
        px: 1.25,
        py: 0.375,
        whiteSpace: 'nowrap',
        width: 'fit-content',
      }}
    >
      {tone?.spin ? (
        <CircleNotchIcon size={11} color={fg} weight="bold" style={{ animation: `${spin} 1.1s linear infinite` }} />
      ) : (
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: fg }} />
      )}
      <Typography component="span" sx={{ fontSize: 11.5, fontWeight: 500, lineHeight: 1.4, color: fg }}>
        {label}
      </Typography>
    </Box>
  );
});
